import React, { useState, useEffect, useMemo } from "react";
import "../styles/App.css";
import { ensureAppSettings, saveAppSettings, cascadeDeleteDossierType } from "../services/data";

const uid = () => Math.random().toString(36).slice(2, 10);

const FIELD_TYPES = [
  { value: "text", label: "Texte" },
  { value: "number", label: "Nombre" },
  { value: "email", label: "Email" },
  { value: "date", label: "Date" },
  { value: "select", label: "Liste déroulante" },
  { value: "checkbox", label: "Case à cocher" },
];

export default function EditDossiers({ user, onBack, onSaved }) {
  // État principal
  const [dossierName, setDossierName] = useState("Dossiers");
  const [dossierDesc, setDossierDesc] = useState(
    "Organisez vos dossiers (contrats, factures, etc.)"
  );
  const [dossierTypes, setDossierTypes] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      const app = await ensureAppSettings(user.uid);
      const data = app?.dossiers || null;
      if (data) {
        setDossierName(data.main?.label || "Dossiers");
        setDossierDesc(data.desc || "");
        // !NORMALISATION: Firestore (label/icon "fas fa-...") -> Éditeur (name/icon sans préfixe)
        const types = (data.types || []).map((t, i) => ({
          id: t.id || uid(), // génère si absent
          name: t.name || t.label || "", // label -> name
          key: t.key || "",
          icon: (t.icon || "fas fa-folder").replace(
            /^(fas|far|fal|fab)\sfa-/,
            ""
          ), // "fas fa-folder" -> "folder"
          color: t.color || "#3b82f6",
          order: Number(t.order ?? i),
          visible: t.visible !== false,
        }));
        setDossierTypes(types);
        setCustomFields(
          Array.isArray(data.fields)
            ? data.fields.map((f) => ({ id: f.id || uid(), ...f }))
            : []
        );
        console.log("[puzzle] EditDossiers loaded (normalisé):", {
          types,
          fields: data.fields,
        });
        // [wrench] Persister la normalisation pour figer id/name/icon au format attendu
        onSaveAll(
          types,
          Array.isArray(data.fields)
            ? data.fields.map((f) => ({ id: f.id || uid(), ...f }))
            : []
        );
      }
    })();
  }, [user?.uid]);

  // Modales + drafts
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [typeDraft, setTypeDraft] = useState({
    name: "",
    key: "",
    icon: "folder",
    color: "#3b82f6",
    order: 1,
  });
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [fieldDraft, setFieldDraft] = useState({
    name: "",
    key: "",
    type: "text",
    options: "",
    required: false,
    order: 1,
    applicableIds: [],
  });
  // Filtres "Champs personnalisés"
  const [qFields, setQFields] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // 'all' | 'none' | '<typeKey>'
  // Map <typeKey> -> <typeId> pour tolérer d'anciennes données qui auraient stocké des ids
  const typeIdByKey = useMemo(
    () => Object.fromEntries(dossierTypes.map((t) => [t.key, t.id])),
    [dossierTypes]
  );
  // Tri
  const sortedTypes = useMemo(
    () =>
      [...dossierTypes].sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)
      ),
    [dossierTypes]
  );
  const sortedFields = useMemo(
    () =>
      [...customFields].sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)
      ),
    [customFields]
  );
  const filteredFields = useMemo(() => {
    let arr = sortedFields;

    // Filtre par type sélectionné
    if (typeFilter === "none") {
      arr = arr.filter((f) => !f.applicableIds || f.applicableIds.length === 0);
    } else if (typeFilter !== "all") {
      arr = arr.filter((f) => {
        const ids = f.applicableIds || [];
        // accepte key OU ancien id (rétro-compat)
        return (
          ids.includes(typeFilter) || ids.includes(typeIdByKey[typeFilter])
        );
      });
    }

    // Recherche plein-texte (name, key, type, options, noms de types applicables)
    const q = qFields.trim().toLowerCase();
    if (q) {
      arr = arr.filter((f) => {
        const applicableNames = (f.applicableIds || [])
          .map((k) => dossierTypes.find((t) => t.key === k)?.name || "")
          .join(" ");
        return [f.name, f.key, f.type, f.options, applicableNames].some((v) =>
          (v ?? "").toString().toLowerCase().includes(q)
        );
      });
    }

    // Tri existant déjà fait dans sortedFields -> on renvoie tel quel
    return arr;
  }, [sortedFields, typeFilter, qFields, dossierTypes, typeIdByKey]);

  // CRUD Types
  const openAddType = () => {
    setEditingTypeId(null);
    setTypeDraft({
      name: "",
      key: "",
      icon: "folder",
      color: "#3b82f6",
      order: (dossierTypes.length || 0) + 1,
    });
    setShowTypeModal(true);
  };

  const openEditType = (t) => {
    setEditingTypeId(t.id);
    setTypeDraft({ ...t });
    setShowTypeModal(true);
  };

  const removeType = (id) => {
    const removed = dossierTypes.find((t) => t.id === id);
    const removedKey = removed?.key || id;
    // Calcule nextTypes + nextFields puis persiste dans la même tick (pas de setTimeout)
    const nextTypes = dossierTypes.filter((t) => t.id !== id);
    const nextFields = customFields.map((f) => ({
      ...f,
      applicableIds: (f.applicableIds || []).filter(
        (x) => x !== id && x !== removedKey
      ),
    }));
    setDossierTypes(nextTypes);
    setCustomFields(nextFields);
    onSaveAll(nextTypes, nextFields);
    if (user?.uid && removedKey) {
      cascadeDeleteDossierType(user.uid, removedKey).catch((err) => {
        console.error(
          `[EditDossiers] cascade delete failed for ${removedKey}:`,
          err?.message || err
        );
      });
    }
  };

  const saveType = () => {
    const { name, key, icon, color, order } = typeDraft;
    if (!name.trim()) return alert("Le nom est requis.");
    if (!key.trim()) return alert("La clé est requise.");
    // Unicité de la clé de type
    const dup = dossierTypes.some(
      (t) => t.id !== editingTypeId && t.key === key
    );
    if (dup) {
      alert("Cette clé de dossier est déjà utilisée.");
      return;
    }
    if (editingTypeId) {
      // Migration si la key change
      const old = dossierTypes.find((t) => t.id === editingTypeId);
      const oldKey = old?.key;
      const nextTypes = dossierTypes.map((t) =>
        t.id === editingTypeId
          ? { ...t, name, key, icon, color, order: Number(order) || 0 }
          : t
      );
      if (oldKey && oldKey !== key) {
        const migratedFields = customFields.map((f) => ({
          ...f,
          applicableIds: (f.applicableIds || []).map((x) =>
            x === oldKey ? key : x
          ),
        }));
        setDossierTypes(nextTypes);
        setCustomFields(migratedFields);
        onSaveAll(nextTypes, migratedFields);
      } else {
        setDossierTypes(nextTypes);
        onSaveAll(nextTypes, customFields);
      }
    } else {
      const id = uid();
      setDossierTypes((prev) => {
        const next = [
          ...prev,
          { id, name, key, icon, color, order: Number(order) || 0 },
        ];
        onSaveAll(next, customFields); // [ok] idem
        return next;
      });
    }

    setShowTypeModal(false);
    setEditingTypeId(null);
  };

  // CRUD Champs
  const openAddField = () => {
    setEditingFieldId(null);
    setFieldDraft({
      name: "",
      key: "",
      type: "text",
      options: "",
      required: false,
      order: (customFields.length || 0) + 1,
      applicableIds: [],
    });
    setShowFieldModal(true);
  };

  const openEditField = (f) => {
    setEditingFieldId(f.id);
    setFieldDraft({
      name: f.name,
      key: f.key,
      type: f.type,
      options: f.options || "",
      applicableIds: f.applicableIds || [],
      required: !!f.required,
      order: f.order ?? 1,
    });
    setShowFieldModal(true);
  };

  const removeField = (idOrKey) => {
    if (!confirm("Supprimer ce champ personnalisé ?")) return;
    setCustomFields((prev) => {
      const next = prev.filter((f) => f.id !== idOrKey && f.key !== idOrKey);
      // on sauvegarde avec l'état à jour
      onSaveAll(dossierTypes, next);
      return next;
    });
  };

  const saveField = () => {
    const { name, key, type, options, required, order, applicableIds } =
      fieldDraft;
    if (!name.trim()) return alert("Le nom est requis.");
    if (!key.trim()) return alert("La clé est requise.");
    // Normaliser options: accepter "," ou ";" puis re-joindre avec ","
    const normOptions = (options || "")
      .split(/[;,]/g)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(",");

    const payload = {
      id: editingFieldId || uid(),
      name,
      key,
      type,
      options: type === "select" || type === "checkbox" ? normOptions : "",
      required: !!required,
      order: Number(order) || 0,
      // [warning] on stocke des KEYS; si jamais un id est passé, on le convertit en key
      applicableIds: (applicableIds || []).map((x) => {
        const t = dossierTypes.find((t) => t.id === x);
        return t ? t.key : x;
      }),
    };

    if (editingFieldId) {
      setCustomFields((prev) => {
        const next = prev.map((f) => (f.id === editingFieldId ? payload : f));
        onSaveAll(dossierTypes, next); // [ok]
        return next;
      });
    } else {
      setCustomFields((prev) => {
        const next = [...prev, payload];
        onSaveAll(dossierTypes, next); // [ok]
        return next;
      });
    }

    setShowFieldModal(false);
    setEditingFieldId(null);
  };

  // Sauvegarde globale
  const onSaveAll = async (
    typesOverride = dossierTypes,
    fieldsOverride = customFields
  ) => {
    if (!user?.uid) {
      alert(
        "[warning] Utilisateur non connecté - impossible d'enregistrer sur Firestore"
      );
      return;
    }

    // normalisation types
    const safeTypes = (typesOverride || []).map((t) => ({
      id: t.id || uid(),
      label: t.name ?? "",
      icon: t.icon
        ? `fas fa-${t.icon.replace(/^(fas|far|fal|fab)\sfa-/, "")}`
        : "fas fa-folder",
      key: t.key ?? "",
      visible: t.visible !== false,
      order: Number(t.order ?? 0),
    }));

    // normalisation champs personnalisés
    const safeFields = (fieldsOverride || []).map((f) => ({
      id: f.id ?? uid(),
      name: f.name ?? "",
      key: f.key ?? "",
      type: f.type ?? "text",
      options: f.options ?? "",
      required: !!f.required,
      order: Number(f.order ?? 0),
      // on stocke des KEYS (pas des ids)
      applicableIds: Array.isArray(f.applicableIds) ? f.applicableIds : [],
    }));

    const settings = {
      main: {
        label: dossierName || "Dossiers",
        icon: "fas fa-folder",
        defaultOpen: true,
      },
      desc: dossierDesc ?? "",
      types: safeTypes,
      fields: safeFields,
      updatedAt: Date.now(),
    };

    try {
      console.log("[disk] Sauvegarde app_settings.dossiers:", settings);
      await saveAppSettings(user.uid, { dossiers: settings }); // <-- la bonne variable
      onSaved?.();
    } catch (e) {
      console.error("[x] Échec sauvegarde dossiers:", e);
      alert("Échec de la sauvegarde. Voir console.");
    }
  };

  return (
    <div className="edit-dossiers">
      <div className="page-header">
        <h1 className="page-title">Configuration des Dossiers</h1>
        <div className="page-actions">
          <button className="btn btn-outline" onClick={onBack}>
            <i className="fas fa-arrow-left"></i> Retour
          </button>
          <button className="btn btn-primary" onClick={onSaveAll}>
            <i className="fas fa-save"></i> Enregistrer
          </button>
        </div>
      </div>

      {/* Nom + Description */}
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Nom principal & description</h3>
        </div>
        <div className="config-body">
          <div className="form-group">
            <label>Nom principal</label>
            <input
              type="text"
              className="form-input"
              value={dossierName}
              onChange={(e) => setDossierName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-input"
              value={dossierDesc}
              onChange={(e) => setDossierDesc(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Types de dossiers */}
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Types de dossiers</h3>
          <button className="btn btn-primary" onClick={openAddType}>
            <i className="fas fa-plus"></i> Ajouter
          </button>
        </div>
        <div className="config-body">
          {sortedTypes.map((t) => (
            <div key={t.id} className="list-item">
              <div className="list-item-content">
                <div className="list-item-title">{t.name}</div>
                <div className="list-item-subtitle">
                  Key: {t.key} - Icône: {t.icon} - Ordre: {t.order}
                </div>
              </div>
              <div className="list-item-actions">
                <div className="action-btn" onClick={() => openEditType(t)}>
                  <i className="fas fa-edit"></i>
                </div>
                <div
                  className="action-btn delete"
                  onClick={() => removeType(t.id)}
                >
                  <i className="fas fa-trash"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Champs personnalisés */}
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Champs personnalisés</h3>
          <button className="btn btn-primary" onClick={openAddField}>
            <i className="fas fa-plus"></i> Ajouter
          </button>
        </div>

        {/* --- Filtres --- */}
        <div className="filters-grid" style={{ marginTop: 8 }}>
          <div className="filter-group">
            <label className="filter-label">Rechercher un champ</label>
            <input
              className="form-input"
              placeholder="Nom, clé, type, options..."
              value={qFields}
              onChange={(e) => setQFields(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Type de dossier</label>
            <select
              className="form-input"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">Tous les types</option>
              <option value="none">Non assignés</option>
              {sortedTypes.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="config-body">
          {filteredFields.map((f) => {
            const applicable = f.applicableIds
              ?.map(
                (key) => dossierTypes.find((t) => t.key === key)?.name || ""
              )
              .filter((n) => n !== "");

            return (
              <div key={f.id} className="list-item">
                <div className="list-item-content">
                  <div className="list-item-title">{f.name}</div>
                  <div className="list-item-subtitle">
                    Type: {f.type} - Key: {f.key} - Ordre: {f.order} -
                    Applicable:{" "}
                    {applicable?.length ? applicable.join(", ") : "Tous"}
                  </div>
                </div>
                <div className="list-item-actions">
                  <div className="action-btn" onClick={() => openEditField(f)}>
                    <i className="fas fa-edit"></i>
                  </div>
                  <div
                    className="action-btn delete"
                    onClick={() => removeField(f.id)}
                  >
                    <i className="fas fa-trash"></i>
                  </div>
                </div>
              </div>
            );
          })}

          {/* État vide */}
          {filteredFields.length === 0 && (
            <div
              style={{ textAlign: "center", color: "#6b7280", marginTop: 8 }}
            >
              Aucun champ pour ce filtre.
            </div>
          )}
        </div>
      </div>
      {/* MODALE Type */}
      {/* MODALE Type */}
      {showTypeModal && (
        <div
          className="formula-helper-overlay show"
          onClick={(e) => {
            if (e.target.classList.contains("formula-helper-overlay"))
              setShowTypeModal(false);
          }}
        >
          <div
            className="formula-helper-modal full-page-form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="formula-helper-header">
              <h3>
                <i className="fas fa-folder"></i>
                {editingTypeId ? "Modifier un type" : "Ajouter un type"}
              </h3>
              <button
                className="formula-close-btn"
                onClick={() => setShowTypeModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="formula-helper-content full-page-content">
              <div className="full-page-form-grid">
                <div className="form-group">
                  <label className="form-label full-page-label">Nom</label>
                  <input
                    type="text"
                    className="form-input"
                    value={typeDraft.name}
                    onChange={(e) =>
                      setTypeDraft({ ...typeDraft, name: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Clé</label>
                  <input
                    type="text"
                    className="form-input"
                    value={typeDraft.key}
                    onChange={(e) =>
                      setTypeDraft({ ...typeDraft, key: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Icône</label>
                  <input
                    type="text"
                    className="form-input"
                    value={typeDraft.icon}
                    onChange={(e) =>
                      setTypeDraft({ ...typeDraft, icon: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Couleur</label>
                  <input
                    type="color"
                    className="form-input"
                    value={typeDraft.color}
                    onChange={(e) =>
                      setTypeDraft({ ...typeDraft, color: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Ordre</label>
                  <input
                    type="number"
                    className="form-input"
                    value={typeDraft.order}
                    onChange={(e) =>
                      setTypeDraft({
                        ...typeDraft,
                        order: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div className="modal-footer full-page-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTypeModal(false)}
                >
                  <i className="fas fa-times"></i> Annuler
                </button>
                <button className="btn btn-primary" onClick={saveType}>
                  <i className="fas fa-save"></i> Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALE Champ */}
      {showFieldModal && (
        <div
          className="formula-helper-overlay show"
          onClick={(e) => {
            if (e.target.classList.contains("formula-helper-overlay"))
              setShowFieldModal(false);
          }}
        >
          <div
            className="formula-helper-modal full-page-form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="formula-helper-header">
              <h3>
                <i className="fas fa-tag"></i>
                {editingFieldId ? "Modifier un champ" : "Ajouter un champ"}
              </h3>
              <button
                className="formula-close-btn"
                onClick={() => setShowFieldModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="formula-helper-content full-page-content">
              <div className="full-page-form-grid">
                <div className="form-group">
                  <label className="form-label full-page-label">Nom</label>
                  <input
                    type="text"
                    className="form-input"
                    value={fieldDraft.name}
                    onChange={(e) =>
                      setFieldDraft({ ...fieldDraft, name: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Clé</label>
                  <input
                    type="text"
                    className="form-input"
                    value={fieldDraft.key}
                    onChange={(e) =>
                      setFieldDraft({ ...fieldDraft, key: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label full-page-label">
                    Type de champ
                  </label>
                  <select
                    className="form-select"
                    value={fieldDraft.type}
                    onChange={(e) =>
                      setFieldDraft({ ...fieldDraft, type: e.target.value })
                    }
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {(fieldDraft.type === "select" ||
                  fieldDraft.type === "checkbox") && (
                  <div className="form-group full-width">
                    <label className="form-label full-page-label">
                      Options (séparées par ;)
                    </label>
                    <textarea
                      className="form-input full-page-textarea"
                      value={fieldDraft.options}
                      onChange={(e) =>
                        setFieldDraft({
                          ...fieldDraft,
                          options: e.target.value,
                        })
                      }
                      rows={4}
                      placeholder="Ex: Option 1; Option 2; Option 3"
                    />
                  </div>
                )}

                <div className="form-group full-width">
                  <label className="form-label full-page-label">
                    Applicable aux types
                  </label>
                  <div className="applicability-grid full-page-applicability">
                    {sortedTypes.map((t) => {
                      const checked = fieldDraft.applicableIds?.includes(t.key);
                      return (
                        <label
                          key={t.key}
                          className="applicability-item full-page-applicability-item"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = new Set(
                                fieldDraft.applicableIds || []
                              );
                              if (e.target.checked) next.add(t.key);
                              else next.delete(t.key);
                              setFieldDraft({
                                ...fieldDraft,
                                applicableIds: Array.from(next),
                              });
                            }}
                          />
                          <i
                            className={`fas fa-${t.icon}`}
                            style={{ marginRight: 6 }}
                          />{" "}
                          {t.name}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label full-page-label">Ordre</label>
                  <input
                    type="number"
                    className="form-input"
                    value={fieldDraft.order}
                    onChange={(e) =>
                      setFieldDraft({
                        ...fieldDraft,
                        order: Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label full-page-checkbox">
                    <input
                      type="checkbox"
                      checked={fieldDraft.required || false}
                      onChange={(e) =>
                        setFieldDraft({
                          ...fieldDraft,
                          required: e.target.checked,
                        })
                      }
                    />
                    <span>Champ obligatoire</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer full-page-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowFieldModal(false)}
                >
                  <i className="fas fa-times"></i> Annuler
                </button>
                <button className="btn btn-primary" onClick={saveField}>
                  <i className="fas fa-save"></i> Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
